import useLockedUntilTimestamp from '@/application/staking/useLockedUntilTimestamp';
import useSweepPower from '@/application/staking/useSweepPower';
import { TopVoter } from '@/core/user';
import CollectionImage from '@/presentation/components/elements/CollectionImage';
import User from '@/presentation/components/elements/User';
import {
  ArrowPathIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import Link from 'next/link';
import { useState } from 'react';
import { QueryStatus } from '@tanstack/react-query';
import { useFormatter, useTranslations } from 'use-intl';
import { Chain, formatEther } from 'viem';
import { absBigInt } from '@/utils/bigint';
import Tooltip from '@/presentation/components/elements/Tooltip';

const PAGE_SIZE = 10;

const VoterHistoryRow = ({
  id,
  activeVotes,
  voteLock,
  chain,
}: TopVoter & { chain: Chain }) => {
  const { number, dateTime } = useFormatter();

  const { data: lockedUntil } = useLockedUntilTimestamp({ userAddress: id });

  const lockTime = lockedUntil ? Number(lockedUntil) * 1000 : undefined;

  return (
    <tr key={id}>
      <td className="py-2 pr-4">
        <hgroup>
          <h3 className="flex items-center">
            <Link
              href={`${chain.blockExplorers?.default.url}/address/${id}`}
              className="hover:underline"
              target="_blank"
              rel="nofollow noreferrer"
            >
              <User address={id} />
            </Link>
          </h3>
        </hgroup>
      </td>
      <td className="py-2 px-4">
        <Tooltip
          labelClassName="inline-flex cursor-help"
          label={
            <div className="flex -space-x-4">
              {activeVotes.map((w, i: number, a: any) => (
                <span
                  className={`flex-none w-12 h-12 bg-brand-pink rounded-lg overflow-hidden inline-flex border-4 ${
                    BigInt(w.votesCast) > 0n
                      ? 'border-brand-blue'
                      : 'border-brand-red'
                  }`}
                  // stack on top of each other visually
                  style={{ zIndex: a.length - i }}
                  key={w.id}
                >
                  <CollectionImage collection={w.collection.id} diameter={40} />
                </span>
              ))}
            </div>
          }
          content={
            <table>
              <thead>
                <tr>
                  <th className="p-2 text-left">{'Collection'}</th>
                  <th className="p-2 text-right">{'Votes'}</th>
                </tr>
              </thead>
              <tbody>
                {activeVotes.map((vote) => {
                  return (
                    <tr key={vote.id}>
                      <td className="p-2">
                        <div className="flex items-center">
                          <CollectionImage
                            collection={vote.collection.id}
                            diameter={32}
                            className="w-8 h-8 mr-2 flex-none"
                          />
                          <span className="whitespace-nowrap">
                            {vote.collection.name}
                          </span>
                        </div>
                      </td>
                      <td className="p-2 text-right">
                        {number(Number(formatEther(BigInt(vote.votesCast))), {
                          maximumFractionDigits: 0,
                        })}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          }
        />
      </td>
      <td className="py-2 px-4 text-right">
        {lockTime
          ? dateTime(lockTime || 0, {
              day: '2-digit',
              month: 'short',
              year: '2-digit',
            })
          : '-'}
      </td>
      <td className="py-2 pl-4 text-right">
        {number(
          Number(
            formatEther(
              activeVotes.reduce(
                (acc, cur) => acc + absBigInt(BigInt(cur.votesCast)),
                0n,
              ),
            ),
          ),
          {
            maximumFractionDigits: 1,
            signDisplay: 'never',
          },
        )}
      </td>
    </tr>
  );
};

const VoterHistoryTable = ({
  voters = [],
  chain,
  status,
}: {
  voters?: TopVoter[];
  chain: Chain;
  status: QueryStatus;
}) => {
  const [offset, setOffset] = useState(0);
  const t = useTranslations('History.VoterHistoryTable');

  const visibleVoters = voters?.slice(offset, offset + PAGE_SIZE);

  return (
    <div className="overflow-x-scroll hide-scroll">
      <table className="w-full">
        <thead className="text-gray-200 text-left text-xs">
          <tr>
            <th className="py-2 pr-4 font-medium">{t('user')}</th>
            <th className="py-2 px-4 font-medium">{t('voted')}</th>
            <th className="py-2 px-4 font-medium text-right">
              {t('lockTime')}
            </th>
            <th className="py-2 pl-4 font-medium text-right">{t('cast')}</th>
          </tr>
        </thead>
        <tbody className="text-sm">
          {visibleVoters.map((x) => (
            <VoterHistoryRow key={x.id} {...x} chain={chain} />
          ))}
          {status === 'pending' && (
            <tr>
              <td colSpan={4} className="p-6 text-center">
                <ArrowPathIcon className="animate-spin h-6 w-6 inline-flex text-gray-200" />
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <div className="flex items-center mt-4">
        <button
          onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
          type="button"
          disabled={offset <= 0}
          className="disabled:opacity-40"
        >
          <ChevronLeftIcon className="text-white h-4 w-4" />
        </button>
        <div className="flex-1 text-xs text-center text-gray-200">
          {`Page ${offset / PAGE_SIZE + 1} of ${Math.ceil(
            voters.length / PAGE_SIZE,
          )}`}
        </div>
        <button
          onClick={() => setOffset(Math.min(voters.length, offset + PAGE_SIZE))}
          type="button"
          disabled={offset >= voters.length - PAGE_SIZE}
          className="disabled:opacity-40"
        >
          <ChevronRightIcon className="text-white h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default VoterHistoryTable;
